import {
  type UVInsertConfig,
  type UVInsertFaceElementTextListConfig,
  getUvInsertTextListKeys,
  isUvInsertTextListElement,
  isUvInsertTextListYear
} from '@shared/models/uv-inserts';

import type {
  PersonalisationSlot,
  PersonalisationSlots,
  PersonalisationStep,
  PersonalisationSteps,
  SelectionSlot
} from '../context/personalisation-typedefs';
import { buildPairedSlotModel } from './buildPairedSlotModel';

// ─── Constants ────────────────────────────────────────────────────────────────

/** The slot mode of the Story of Us step whose rows the card mirrors. */
const PAIRED_SLOT_MODE = 'PAIRED';

/** The variant option that names the month, when the birthstones are authored with options. */
const MONTH_OPTION_NAME = 'month';

/** Shopify's title for the only variant of a single-variant product. */
const DEFAULT_VARIANT_TITLE = 'Default Title';

/**
 * A trailing " – <stone>" / " - <stone>" suffix on a birthstone label ("January – Garnet").
 * The card prints the month only, so the stone name is stripped.
 */
const STONE_SUFFIX_PATTERN = /\s[–-]\s.*$/;

// ─── Public types ─────────────────────────────────────────────────────────────

/** The variant fields the month label reads; the context's variants Map carries more. */
export interface MonthVariant {
  title?: string;
  /** Present on the context's variants Map — the month lives here for per-product birthstones. */
  product?: { title?: string };
  selectedOptions?: Array<{ name?: string; value?: string }>;
}

export interface DeriveStoryCardTextOptions {
  /** `personalisationSteps` from the context (a Map), or the same steps as an array. */
  steps: PersonalisationSteps | PersonalisationStep[] | null | undefined;
  /** `personalisationSlots` from the context (a Map), or the same slots as an array. */
  slots: PersonalisationSlots | PersonalisationSlot[] | null | undefined;
  /** `selections.slots` from the context. */
  selections: SelectionSlot[] | null | undefined;
  /** `variants` from the context, keyed by variant GID. */
  variants: ReadonlyMap<string, MonthVariant> | null | undefined;
  /** The card's `TEXT_LIST` element — supplies the key prefixes and the row cap. */
  element: UVInsertFaceElementTextListConfig;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function toArray<T>(source: Map<string, T> | T[] | null | undefined): T[] {
  if (!source) return [];
  return Array.isArray(source) ? source : Array.from(source.values());
}

/** Extracts the trimmed text from an ALPHANUMERIC selection, which may be a plain string. */
function getSelectedText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object' && 'text' in value) {
    const { text } = value as { text?: unknown };
    return typeof text === 'string' ? text.trim() : '';
  }
  return '';
}

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * The month a birthstone variant stands for, as printed on the keepsake card.
 *
 * Prefers the variant's `Month` option when the birthstones are authored with options, and
 * otherwise reads the variant title. Either way a trailing stone name is stripped, so
 * "January – Garnet" prints as "January" and "July" prints unchanged.
 *
 * @param options - Options object.
 * @param options.variant - The selected birthstone variant, if any.
 * @returns The month label, or an empty string when there is no variant to read.
 */
export function monthLabel({ variant }: { variant: MonthVariant | undefined | null }): string {
  if (!variant) return '';

  const monthOption = variant.selectedOptions?.find(
    (option) => (option?.name ?? '').trim().toLowerCase() === MONTH_OPTION_NAME
  );

  // A birthstone authored as its own product has a single "Default Title" variant, so its month
  // is on the product. Same fallback as the step UI's getVariantTitle (usePairedSlotViewModel),
  // SequenceViewer, ComponentGroups and processLineItems — without it the card prints
  // "Default Title" wherever the stones are one product per month.
  const productTitle = variant.product?.title?.trim();
  const titleLabel =
    variant.title === DEFAULT_VARIANT_TITLE && productTitle ? productTitle : (variant.title ?? '');

  const rawLabel = (monthOption?.value ?? titleLabel).trim();

  return rawLabel.replace(STONE_SUFFIX_PATTERN, '').trim();
}

/**
 * The config's `TEXT_LIST` element, if it has one.
 *
 * Callers need this before they can derive anything — it is what says the config is a Story of
 * Us card at all — so it lives beside the derive rather than inside a component view model.
 *
 * @param options - Options object.
 * @param options.config - A resolved UV insert config.
 * @returns The first `TEXT_LIST` element across all faces, or null when there is none.
 */
export function findUvInsertTextListElement({
  config
}: {
  config: UVInsertConfig | null | undefined;
}): UVInsertFaceElementTextListConfig | null {
  if (!config) return null;
  for (const face of config.faceElements) {
    for (const element of face.elementConfig) {
      if (isUvInsertTextListElement(element)) return element;
    }
  }
  return null;
}

/**
 * Folds a fresh derive into a stored `textByKey`, or reports that nothing changed.
 *
 * Every key the element owns — each row's month, year (when the element has a `yearKeyPrefix`)
 * and moment — is host-derived from the PAIRED step's slots, so each takes the derived value or
 * `''`. A key the derive no longer produces is set to `''` rather than removed, matching how the
 * host view model seeds every `TEXT_LIST` key (empty values are dropped from the line item
 * properties in `buildPersonalisationChoices`). A row whose stone is cleared derives nothing, so
 * its year and moment go with its month and the cart matches what the card prints. Keys the
 * element does not own (the caption, the message) are copied through untouched.
 *
 * Returning `null` for an unchanged map is what lets callers guard a `setState` and never loop:
 * PersonalisationContext re-derives on every selection change and only writes a non-null patch.
 *
 * @param options - Options object.
 * @param options.textByKey - The stored map, if any.
 * @param options.derived - A fresh `deriveStoryCardText` result.
 * @param options.element - The card's `TEXT_LIST` element — supplies the owned keys.
 * @returns A patched copy, or null when every owned key already holds the derived value.
 */
export function mergeDerivedStoryCardText({
  textByKey,
  derived,
  element
}: {
  textByKey: Record<string, string> | null | undefined;
  derived: Record<string, string>;
  element: UVInsertFaceElementTextListConfig;
}): Record<string, string> | null {
  const current = textByKey ?? {};
  const next: Record<string, string> = { ...current };
  let changed = false;

  for (const { monthKey, yearKey, momentKey } of getUvInsertTextListKeys({ element })) {
    for (const key of [monthKey, yearKey, momentKey]) {
      if (!key) continue;
      const value = derived[key] ?? '';
      if ((current[key] ?? '') === value) continue;
      next[key] = value;
      changed = true;
    }
  }

  return changed ? next : null;
}

/**
 * Derives the keepsake card's month, year and moment lines from the PAIRED step's selections.
 *
 * Reads the first step in `PAIRED` slot mode, orders its slots into rows with the same
 * `buildPairedSlotModel` the step UI uses, and for each row whose stone is chosen writes
 * `${monthKeyPrefix}${i}`, plus `${yearKeyPrefix}${i}` from the row's year slot and
 * `${momentKeyPrefix}${i}` from its moment slot when they hold text. The year is written only when
 * it is exactly four digits (`isUvInsertTextListYear`) — the card never prints a half-typed year,
 * so the cart must not carry one. Nothing is written for an empty value, and a row with no stone
 * is skipped entirely: an orphan year or moment has no month to sit under.
 *
 * `null` and `{}` are deliberately different answers, because merging `{}` blanks every owned key:
 *
 * - `{}` — the PAIRED step is there and not one stone is chosen. The card is genuinely empty.
 * - `null` — there is no PAIRED step to read at all (the context has not loaded, or variant-driven
 *   filtering hid the stones step while the card's UV_INSERT slot survived). Nothing is known
 *   about the rows, so callers must leave the stored text exactly as it is.
 *
 * @param options - Options object.
 * @returns The derived `textByKey` slice for the element, or `null` when there is no PAIRED step.
 */
export function deriveStoryCardText(
  options: DeriveStoryCardTextOptions
): Record<string, string> | null {
  const { steps, slots, selections, variants, element } = options;

  // Absence, not emptiness: see the note above — inferring "no step" from an empty result would
  // let a hidden step blank the card's rows for good.
  const pairedStep = toArray(steps).find((step) => step?.slotMode === PAIRED_SLOT_MODE);
  if (!pairedStep) return null;

  // Mirrors PersonalisationStep's useViewModel: a step names its slots by id.
  const slotsById = new Map(toArray(slots).map((slot) => [slot.id, slot]));
  const stepSlots = (pairedStep.slots ?? [])
    .map((slotId) => slotsById.get(slotId))
    .filter((slot): slot is PersonalisationSlot => slot != null);

  const { rows } = buildPairedSlotModel({ slots: stepSlots });

  const selectionsBySlotId = new Map((selections ?? []).map((selection) => [selection.id, selection]));
  const getSelected = (slotId: string | undefined): unknown =>
    slotId ? (selectionsBySlotId.get(slotId)?.selected ?? null) : null;

  const textByKey: Record<string, string> = {};

  for (const { index, componentSlot, yearSlot, textSlot } of rows.slice(0, element.rows)) {
    const rowNumber = index + 1;

    const selectedVariantId = getSelected(componentSlot.id);
    const variant = typeof selectedVariantId === 'string' && selectedVariantId
      ? variants?.get?.(selectedVariantId)
      : undefined;
    const month = monthLabel({ variant });
    if (!month) continue;

    textByKey[`${element.monthKeyPrefix}${rowNumber}`] = month;

    const year = getSelectedText(getSelected(yearSlot?.id));
    if (element.yearKeyPrefix && isUvInsertTextListYear(year)) {
      textByKey[`${element.yearKeyPrefix}${rowNumber}`] = year;
    }

    const moment = getSelectedText(getSelected(textSlot?.id));
    if (moment) {
      textByKey[`${element.momentKeyPrefix}${rowNumber}`] = moment;
    }
  }

  return textByKey;
}
